<!--
    One rack load in the delivery menu.

    72 px minimum: three lines of content need it anyway, and it lands well
    above the 48 px touch floor. The whole row is the target — there is nothing
    smaller inside it to hit.

    Nothing here is a chip. The rack setup, the location and the date are facts
    about the load, not states, so they read as a small icon and a bold word and
    sit inline after the description, dropping to the next line only when the row
    runs out of width. The state is not a chip either: it is written inside the
    progress bar, which is the one element that already knows where every panel
    of this rack is.

    The prepend icon stays `mdi-bookshelf` whatever the state (icons.md 2): a
    rack load is a rack load all the way through.
-->
<template>
    <!-- `value` is the load. Without it a `v-list-item` registers itself in the
         list under its href, so the identity of a row would be its URL. -->
    <v-list-item link :to="route" :value="trace.uuid" color="accent" class="load-row"
        min-height="72">
        <template v-slot:prepend>
            <v-icon :color="state === 'empty' ? 'grey-lighten-1' : ''">mdi-bookshelf</v-icon>
        </template>

        <!-- Wraps rather than truncates: the description keeps a floor of its
             own, and the facts after it move to a second line before it starts
             losing characters. -->
        <div class="head">
            <span class="name">{{ name }}</span>
            <span v-if="trace.delivery_date" class="fact" :class="{ late }">
                <v-icon size="13">{{ late ? "mdi-calendar-alert" : "mdi-calendar" }}</v-icon>
                <b class="pit-mono">{{ trace.delivery_date }}</b>
            </span>
            <span v-if="trace.rack_name" class="fact">
                <v-icon size="13">mdi-bookshelf</v-icon><b>{{ trace.rack_name }}</b>
            </span>
            <span v-if="trace.location" class="fact">
                <v-icon size="13">mdi-map-marker</v-icon><b>{{ trace.location }}</b>
            </span>
        </div>

        <!-- The bar *is* the state chip. Where the panels of this rack are —
             delivered, in the rack, still to load — with the state written
             across it, so one element answers both questions and the row keeps
             a line for itself.

             The fill sits behind the text at partial alpha rather than at full
             strength: a label has to stay readable over all three colours and
             over the empty track, in both themes, and dimming the fill is what
             buys that without a scrim or a text shadow. -->
        <div class="bar" :class="{ empty: !panels_count }">
            <div class="fill">
                <div v-for="seg in segments" :key="seg.key" class="seg" :class="seg.key"
                    :style="{ width: seg.pct + '%' }" />
            </div>
            <div class="bar-text">
                <v-icon size="13">{{ meta.icon }}</v-icon>
                <!-- The user's own note rides inside the state in brackets
                     rather than beside it as a second chip: on the live database
                     14 of the 19 values in that field are keyboard noise, so it
                     is a footnote to the state, never a state of its own. -->
                <span class="state-text">
                    {{ t(meta.key) }}<template v-if="trace.status"> ({{ trace.status }})</template>
                </span>
                <span class="count pit-mono">
                    <strong>{{ loaded_count }}</strong>/{{ panels_count }}
                </span>
            </div>
        </div>

    </v-list-item>
</template>

<script setup>
import { computed } from "vue";
import { usePitStoreBasics } from "@/stores/app_common";
import {
    rackLoadState,
    rackLoadIsLate,
    RACK_LOAD_STATE_META,
} from "@/components/delivery/delivery_composables";

const { t } = usePitStoreBasics();

const props = defineProps({
    trace: { type: Object, required: true },
});

const state = computed(() => rackLoadState(props.trace));
const late = computed(() => rackLoadIsLate(props.trace, state.value));
const meta = computed(() => RACK_LOAD_STATE_META[state.value]);

const name = computed(() => props.trace.description || t("rack_load"));
const panels_count = computed(
    () => props.trace.panels_count ?? props.trace.assemblies?.length ?? 0);
// `loaded_count` counts in_rack and beyond; `delivered_count` counts delivered
// and mounted. The statuses are a progression, so the exclusive middle band is
// the difference between the two.
const loaded_count = computed(() => props.trace.loaded_count ?? 0);
const delivered_count = computed(() => props.trace.delivered_count ?? 0);

const segments = computed(() => {
    const total = panels_count.value;
    if (!total) return [];
    const delivered = Math.min(delivered_count.value, total);
    const in_rack = Math.max(0, Math.min(loaded_count.value, total) - delivered);
    const to_load = Math.max(0, total - delivered - in_rack);
    return [
        { key: "delivered", pct: (delivered / total) * 100 },
        { key: "in-rack", pct: (in_rack / total) * 100 },
        { key: "to-load", pct: (to_load / total) * 100 },
    ].filter(seg => seg.pct > 0);
});

const route = computed(
    () => ({ name: "rack_load", params: { trace_uuid: props.trace.uuid } }));
</script>

<style scoped>
.load-row {
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);
}

.head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 2px 10px;
}

/* `flex: 1 1 auto` with a floor: the facts after it wrap to their own line
   before the description starts losing characters, and it only ellipsises once
   there is nothing left to wrap. */
.name {
    flex: 1 1 auto;
    min-width: 7rem;
    font-size: 0.9rem;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* An icon and a bold word, not a chip. Chips are for the state. */
.fact {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 0.72rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
    white-space: nowrap;
    max-width: 100%;
    overflow: hidden;
}

.fact b {
    font-weight: 600;
    color: rgba(var(--v-theme-on-surface), 0.85);
    overflow: hidden;
    text-overflow: ellipsis;
}

.fact.late,
.fact.late b {
    color: rgb(var(--v-theme-error));
}

/* Thick enough to hold its own label: the chip it replaced was 20 px, and a
   24 px bar reads as a bar rather than as a rule with text near it. */
.bar {
    position: relative;
    height: 24px;
    border-radius: 4px;
    overflow: hidden;
    margin: 6px 0 2px;
    background: rgba(var(--v-theme-on-surface), 0.08);
}

.fill {
    position: absolute;
    inset: 0;
    display: flex;
}

.seg {
    height: 100%;
}

.seg.delivered {
    background: rgba(var(--v-theme-success), 0.42);
}

.seg.in-rack {
    background: rgba(var(--v-theme-info), 0.38);
}

.seg.to-load {
    background: rgba(var(--v-theme-accent), 0.34);
}

.bar-text {
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 0 8px;
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    color: rgba(var(--v-theme-on-surface), 0.87);
}

.bar.empty .bar-text {
    color: rgba(var(--v-theme-on-surface), 0.45);
    font-weight: 500;
}

.count {
    margin-left: auto;
    flex: 0 0 auto;
    padding-left: 6px;
    font-weight: 400;
    color: rgba(var(--v-theme-on-surface), 0.7);
}

.count strong {
    font-weight: 700;
    color: rgba(var(--v-theme-on-surface), 0.87);
}

.state-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
